/**
 * SEO Service (Frontend)
 * 
 * SINGLE SOURCE OF TRUTH for generating SEO-friendly filenames and alt text.
 * Used by BOTH manual generation (frontend) AND automation (Cloud Function).
 * 
 * IMPORTANT: This file and functions/src/seoService.ts must stay identical!
 */

/**
 * Generate a SEO-friendly filename slug from text.
 * Converts text to lowercase, removes accents and special characters,
 * replaces spaces with hyphens, and limits length.
 */
export function generateSeoFilename(text: string): string {
    return text
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove accents
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single
        .substring(0, 50) // Limit length
        .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}

/**
 * Generate SEO metadata for a product image.
 * THIS IS THE EXACT SAME PROCESS AS THE MANUAL GENERATION.
 * 
 * Process:
 * - filename: {product-slug}-v{existingImageCount + 1}.webp
 * - altText: Product title (exact)
 * 
 * @param productTitle - The product title (required)
 * @param existingImageCount - Number of images already on the product
 * @param extension - File extension (default: webp)
 */
export function generateProductImageSeoMetadata(
    productTitle: string,
    existingImageCount: number = 0,
    extension: string = "webp"
): { filename: string; altText: string } {
    const productSlug = generateSeoFilename(productTitle);
    const version = existingImageCount + 1;

    return {
        filename: `${productSlug}-v${version}.${extension}`,
        altText: productTitle, // Exact product title, no modification
    };
}

/**
 * Fallback for cases without a product (prompt-based naming).
 * Used when no source product is available.
 */
export function generatePromptBasedSeoMetadata(
    prompt: string,
    extension: string = "webp"
): { filename: string; altText: string } {
    const promptSlug = generateSeoFilename(prompt.substring(0, 40));

    return {
        filename: promptSlug ? `${promptSlug}.${extension}` : `product-image-${Date.now()}.${extension}`,
        altText: prompt.substring(0, 100),
    };
}
